import { useState, useEffect, useCallback } from 'react';
import { NotificationToast } from '../../../../../components/organisms/notification-toast';
import Modal, {
	ModalProps,
} from '../../../../../components/template/mvc-modal';
import Toggle from '../../../../../components/molecules/toggle';
import { useTranslation } from 'react-i18next';
import { EditableChangeEvent } from '../../../../../utils/types/editable';
import Input from '../../../../../components/atoms/input';
import HarborService, { IHarbor } from '../../../../../services/Harbor.service';
import Select from '../../../../../components/atoms/select';
import ClientService, { IClient } from '../../../../../services/Client.service';

export interface HarborsModalProps extends ModalProps<IHarbor> {
	setModalProps: React.Dispatch<React.SetStateAction<ModalProps<IHarbor>>>;
}

const HarborsModal = ({
	data,
	setModalProps,
	onClose,
	mode,
	isModalVisible,
	...props
}: HarborsModalProps) => {
	const { t } = useTranslation();

	const [contas, definirContas] = useState<IClient[]>([]);
	const [error, setError] = useState<string>('');
	const contaSelecionada = contas.find((conta) => conta.id === data.client?.id)
		?? data.client;

	const carregarContas = useCallback(async () => {
		const resposta = await ClientService.getAll();
		if (!resposta) {
			setError('alert.error.generic');
			return;
		}
		definirContas(resposta.data);
	}, []);

	useEffect(() => {
		if (isModalVisible) carregarContas();
	}, [isModalVisible, carregarContas]);

	useEffect(() => {
		if (!error) return;
		NotificationToast.Warn(t(error));
		setError('');
	}, [error]);

	const onSubmit = () => {
		if (!validate()) return;
		if (mode === 'edit') {
			onEdit();
			return;
		}
		onCreate();
	};

	const validate = (): boolean => {
		if (
			!data.name ||
			!data.address ||
			!Number.isInteger(data.client?.id) ||
			!data.client?.id ||
			data.client.id < 1
		) {
			setError('alert.error.mandatory');
			return false;
		}
		return true;
	};

	const onCreate = async () => {
		const response = await HarborService.create(data, setError);
		if (!response) return;
		NotificationToast.Success(
			`${t('port_management.harbor')} ${t('alert.success.create')}`
		);
		onClose(true);
	};

	const onEdit = async () => {
		const response = await HarborService.edit(data, setError);
		if (!response) return;
		NotificationToast.Success(
			`${t('port_management.harbor')} ${t('alert.success.update')}`
		);
		onClose(true);
	};

	const handleInput = (
		e: React.ChangeEvent<HTMLInputElement> | EditableChangeEvent
	) => {
		setModalProps((prev) => ({
			...prev,
			data: {
				...prev.data,
				[e.target.name]: e.target.value,
			},
		}));
	};

	const selecionarConta = (evento: EditableChangeEvent) => {
		const conta = contas.find(
			(item) => `${item.fantasyName} - ${item.cnpj}` === evento.target.value
		);
		setModalProps((prev) => ({
			...prev,
			data: { ...prev.data, client: conta },
		}));
	};

	const renderToggles = () => (
		<div className="grid">
			<Toggle
				name="isActive"
				value={data.isActive}
				label={t('status.active')}
				onChange={handleInput}
				disabled={mode === 'view'}
			/>
		</div>
	);

	const renderInputs = () => (
		<div className="grid gap-x-2 gap-y-3">
			<Select
				name="client"
				value={contaSelecionada?.id
					? `${contaSelecionada.fantasyName} - ${contaSelecionada.cnpj}`
					: ''}
				label={t('admin.client')}
				placeholder={t('admin.client')}
				options={contas.map((conta) => `${conta.fantasyName} - ${conta.cnpj}`)}
				onChange={selecionarConta}
				disabled={mode === 'view'}
				required
			/>
			<Input
				value={data.name}
				name="name"
				label={t('misc.name')}
				placeholder={t('misc.name')}
				onChange={handleInput}
				disabled={mode === 'view'}
				required
			/>
			<Input
				value={data.address}
				name="address"
				label={t('misc.address')}
				placeholder={t('misc.address')}
				onChange={handleInput}
				disabled={mode === 'view'}
				required
			/>
		</div>
	);

	return (
		<Modal<IHarbor>
			onClose={onClose}
			data={data}
			mode={mode}
			onConfirm={onSubmit}
			isModalVisible={isModalVisible}
			{...props}
		>
			{renderToggles()}
			{renderInputs()}
		</Modal>
	);
};

export default HarborsModal;
